import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, ListGroup, ListGroupItem, Badge, Button } from "reactstrap";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import apis from "../../../../Api";

function PresenceHistoryModal({ isOpen, onClose, studentId, companyId, onOpenUserDetail, onExport }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [student, setStudent] = useState(null);

  useEffect(() => {
    let mounted = true;

    const safeRequest = async (promise) => {
      try {
        const resp = await promise;
        return { data: resp?.data !== undefined ? resp.data : resp };
      } catch (err) {
        console.error('PresenceHistoryModal request error:', err);
        return { error: err };
      }
    };

    const load = async () => {
      if (!isOpen) return;
      if (!studentId) {
        setData(null);
        setStudent(null);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setData(null);
      setStudent(null);

      try {
        const [presenceResult, usersCompanyResult] = await Promise.all([
          safeRequest(apis.getCompanyPresenceHistory(companyId, studentId)),
          companyId ? safeRequest(apis.getUsersCompany(companyId)) : Promise.resolve({ data: [] })
        ]);

        const payload = presenceResult?.data || {};
        const records = Array.isArray(payload.records) ? payload.records : [];
        const summaryRaw = payload.summary || {};
        const summary = {
          total: Number(summaryRaw.total) || records.length || 0,
          present: Number(summaryRaw.present) || 0,
          absent: Number(summaryRaw.absent) || 0
        };

        const usersList = usersCompanyResult?.data || [];
        const foundUser = usersList.find(u => String(u.id) === String(studentId));
        const profile = foundUser ? {
          id: foundUser.id,
          name: foundUser.name,
          lastName: foundUser.lastName,
          email: foundUser.email,
          photo: foundUser.photo || "/images/defaultAvatar.png",
          roleLabel: foundUser.roleLabel || "Aluno",
          statusLabel: foundUser.statusLabel || (foundUser.isBlocked ? "Bloqueado" : "Ativo"),
          isBlocked: Boolean(foundUser.isBlocked)
        } : { id: studentId, name: "Aluno", lastName: "", email: "", photo: "/images/defaultAvatar.png", roleLabel: "Aluno", statusLabel: null, isBlocked: false };

        if (!mounted) return;
        setData({ records, summary });
        setStudent(profile);
      } catch (err) {
        console.error('Erro ao carregar histórico de presença (modal):', err);
        if (!mounted) return;
        setError('Não foi possível carregar o histórico de presença.');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    load();

    return () => { mounted = false; };
  }, [isOpen, studentId, companyId]);

  if (!isOpen || !student) return null;

  const records = Array.isArray(data?.records) ? data.records : [];
  const presenceRecords = records.filter(r => (r.type || "").toLowerCase() === "presente");
  const absenceRecords = records.filter(r => (r.type || "").toLowerCase() === "falta");
  const hasHistoryRecords = records.length > 0;

  const formatRecordDate = value => {
    if (!value) return "Sem data";
    const parsed = moment(value, ["YYYY-MM-DD", moment.ISO_8601], true);
    if (parsed.isValid()) return parsed.format("DD/MM/YYYY");
    const fallback = moment(value);
    return fallback.isValid() ? fallback.format("DD/MM/YYYY") : "Sem data";
  };

  const handleExport = () => {
    if (!hasHistoryRecords) return;
    const rows = records.map(record => ({
      presenceListId: record.presenceListId || "",
      classroomId: record.classroomId || "",
      classroomName: record.classroomName || "",
      date: record.date || "",
      formattedDate: record.date ? moment(record.date).format("DD/MM/YYYY") : "",
      type: record.type || ""
    }));
    onExport && onExport(rows, student);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Histórico de presença</ModalHeader>
      <ModalBody>
        {student ? (
          <div className="d-flex align-items-center mb-4">
            <div className="mr-3" style={{ width: "56px", height: "56px", borderRadius: "50%", overflow: "hidden" }}>
              <img src={student.photo || "/images/defaultAvatar.png"} alt={student.name || "Usuário"} className="rounded-circle" style={{ width: "56px", height: "56px", objectFit: "cover" }} />
            </div>
            <div>
              <div className="h5 mb-1">{`${student.name || ""} ${student.lastName || ""}`.trim() || "Usuário"}</div>
              <div className="text-sm text-muted">{student.email || "Sem e-mail"}</div>
            </div>
          </div>
        ) : null}

        <Row className="mb-4 text-center">
          <Col md="4" sm="6" className="mb-3">
            <div className="text-sm text-muted">Total de registros</div>
            <div className="h4 font-weight-bold mb-0">{data?.summary?.total || 0}</div>
          </Col>
          <Col md="4" sm="6" className="mb-3">
            <div className="text-sm text-muted">Presenças</div>
            <div className="h4 font-weight-bold mb-0 text-success">{data?.summary?.present || 0}</div>
          </Col>
          <Col md="4" sm="6" className="mb-3">
            <div className="text-sm text-muted">Faltas</div>
            <div className="h4 font-weight-bold mb-0 text-danger">{data?.summary?.absent || 0}</div>
          </Col>
        </Row>

        {error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : null}

        {loading ? (
          <div className="text-center py-5">
            <ClipLoader size={28} color="#5e72e4" />
            <div className="mt-3 text-muted">Carregando histórico...</div>
          </div>
        ) : null}

        {!loading && !error ? (
          <>
            <h6 className="text-uppercase text-muted ls-1 mb-2">Presenças</h6>
            {presenceRecords.length > 0 ? (
              <ListGroup flush className="mb-4">
                {presenceRecords.map(record => (
                  <ListGroupItem key={`presence-record-${record.id || record.date}-present`} className="py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <div className="font-weight-bold">{formatRecordDate(record.date)}</div>
                      {record.classroomName ? (
                        <div className="small text-muted">{record.classroomName}</div>
                      ) : null}
                    </div>
                    <Badge color="success" pill>Presente</Badge>
                  </ListGroupItem>
                ))}
              </ListGroup>
            ) : (
              <div className="text-muted text-center mb-4">Nenhuma presença registrada.</div>
            )}

            <h6 className="text-uppercase text-muted ls-1 mb-2">Faltas</h6>
            {absenceRecords.length > 0 ? (
              <ListGroup flush>
                {absenceRecords.map(record => (
                  <ListGroupItem key={`presence-record-${record.id || record.date}-absent`} className="py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <div className="font-weight-bold">{formatRecordDate(record.date)}</div>
                      {record.classroomName ? (
                        <div className="small text-muted">{record.classroomName}</div>
                      ) : null}
                    </div>
                    <Badge color="danger" pill>Falta</Badge>
                  </ListGroupItem>
                ))}
              </ListGroup>
            ) : (
              <div className="text-muted text-center">Nenhuma falta registrada.</div>
            )}
          </>
        ) : null}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center w-100">
        <div>
          {student ? (
            <Button color="link" onClick={() => onOpenUserDetail && onOpenUserDetail(student)}>Detalhes do usuário</Button>
          ) : null}
        </div>
        <div className="d-flex align-items-center">
          <Button color="primary" onClick={handleExport} disabled={!hasHistoryRecords} className="mr-2">Exportar CSV</Button>
          <Button color="secondary" onClick={onClose}>Fechar</Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default PresenceHistoryModal;
